import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Alert from '../common/Alert';

const AnonymousSurvey = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState(null);
  const [manager, setManager] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });

  useEffect(() => {
    fetchSurvey();
  }, [token]);

  const fetchSurvey = async () => {
    try {
      const response = await fetch(`/api/survey_invites/${token}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load survey');
        setLoading(false);
        return;
      }

      const data = await response.json();
      setSurvey(data.survey);
      setManager(data.manager);
      setQuestions(data.questions);
      
      const initialResponses = {};
      data.questions.forEach(q => {
        initialResponses[q.id] = { rating: 0, comment: '' };
      });
      setResponses(initialResponses);
      setLoading(false);
    } catch (err) {
      setError('An error occurred while loading the survey');
      setLoading(false);
    }
  };

  const handleRatingChange = (questionId, rating) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], rating: parseInt(rating) }
    }));
  };

  const handleCommentChange = (questionId, comment) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], comment }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allRated = questions.every(q => responses[q.id]?.rating > 0);
    if (!allRated) {
      setAlert({ show: true, message: 'Please rate all questions before submitting', type: 'warning' });
      return;
    }

    setSubmitting(true);

    try {
      const responsesArray = Object.entries(responses).map(([questionId, data]) => ({
        question_id: parseInt(questionId),
        rating: data.rating,
        comment: data.comment || null
      }));

      const response = await fetch(`/api/survey_invites/${token}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          survey_response: {
            responses: responsesArray
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit survey');
      }

      setSubmitted(true);
    } catch (err) {
      setAlert({ show: true, message: err.message || 'Failed to submit survey', type: 'error' });
      setSubmitting(false);
    }
  };

  const styles = {
    pageWrapper: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem'
    },
    container: {
      maxWidth: '800px',
      margin: '0 auto'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '2rem',
      boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
      marginBottom: '2rem'
    },
    header: {
      textAlign: 'center',
      color: '#2d3748',
      marginBottom: '2rem'
    },
    questionCard: {
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      padding: '1.5rem',
      marginBottom: '1.5rem'
    },
    ratingContainer: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
      margin: '1rem 0'
    },
    ratingButton: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      border: '2px solid #ddd',
      backgroundColor: 'white',
      cursor: 'pointer',
      fontSize: '1.2rem',
      fontWeight: 'bold',
      transition: 'all 0.2s'
    },
    ratingButtonSelected: {
      backgroundColor: '#07638e',
      color: 'white',
      border: '2px solid #07638e'
    },
    textarea: {
      width: '100%',
      padding: '0.75rem',
      borderRadius: '6px',
      border: '1px solid #ddd',
      fontSize: '1rem',
      marginTop: '1rem',
      fontFamily: 'inherit',
      resize: 'vertical'
    },
    submitButton: {
      width: '100%',
      padding: '1rem',
      backgroundColor: '#07638e',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1.2rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      marginTop: '2rem'
    },
    successMessage: {
      textAlign: 'center',
      padding: '3rem'
    },
    errorMessage: {
      textAlign: 'center',
      padding: '3rem',
      color: '#e53e3e'
    }
  };

  if (loading) {
    return (
      <div style={styles.pageWrapper}>
        <div style={styles.container}>
          <div style={styles.card}>
            <p style={{ textAlign: 'center' }}>Loading survey...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.pageWrapper}>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.errorMessage}>
              <h2>⚠️ Survey Unavailable</h2>
              <p>{error}</p>
              <p style={{ marginTop: '2rem', fontSize: '0.9rem', color: '#666' }}>
                This link may have expired, been used already, or is invalid.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={styles.pageWrapper}>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.successMessage}>
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <img src="/bluecoding-logo-b.png" alt="Blue Coding" style={{ maxWidth: '200px', height: 'auto' }} />
              </div>
              <h1 style={{ textAlign: 'center', fontSize: '2rem', marginBottom: '1rem' }}>🎉 Thank You!</h1>
              <p style={{ fontSize: '1.2rem', marginTop: '1rem', textAlign: 'center' }}>
                Your feedback has been submitted successfully.
              </p>
              <p style={{ marginTop: '2rem', color: '#666', textAlign: 'center' }}>
                Your anonymous responses will help improve management effectiveness.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageWrapper}>
      <Alert 
        show={alert.show}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, show: false })}
      />
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h1>SurveyPilot</h1>
            <h2>{survey.title}</h2>
            <p style={{ color: '#666', marginTop: '0.5rem' }}>{survey.description}</p>
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f0f4ff', borderRadius: '8px' }}>
              <strong>Manager:</strong> {manager.name} ({manager.department})
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {questions.map((question, index) => (
            <div key={question.id} style={styles.card}>
              <div style={styles.questionCard}>
                <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem', fontWeight: '500' }}>{question.text}</p>
                <p style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
                  Category: {question.category}
                </p>

                <div style={styles.ratingContainer}>
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => handleRatingChange(question.id, rating)}
                      style={{
                        ...styles.ratingButton,
                        ...(responses[question.id]?.rating === rating ? styles.ratingButtonSelected : {})
                      }}
                    >
                      {rating}
                    </button>
                  ))}
                </div>
                <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>
                  1 = Strongly Disagree, 5 = Strongly Agree
                </p>

                <textarea
                  style={styles.textarea}
                  rows="3"
                  placeholder="Additional comments (optional)"
                  value={responses[question.id]?.comment || ''}
                  onChange={(e) => handleCommentChange(question.id, e.target.value)}
                />
              </div>
            </div>
          ))}

          <div style={styles.card}>
            <button
              type="submit"
              style={styles.submitButton}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Survey'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnonymousSurvey;
